import { useState } from "react";

import type { TrackingTimeline } from "../../tracking/player/timeline-status";
import type { TRenderableSegment } from "../../utils/tree";

export type SegmentGrowth = { from: number; to: number; startedAt: number };
export const SEGMENT_GROWTH_MS = 220;

export function segmentGrowthEnd(growth: SegmentGrowth, now: number): number {
  const progress = Math.max(0, Math.min(1, (now - growth.startedAt) / SEGMENT_GROWTH_MS));
  return growth.from + (growth.to - growth.from) * (1 - (1 - progress) ** 3);
}

type Extent = { start: number; rawEnd: number; displayedEnd: number; appendPending: boolean };

function extentsFor(segments: readonly TRenderableSegment[], timeline: TrackingTimeline | null): Map<string, Extent> {
  const extents = new Map<string, Extent>();
  for (const segment of segments) {
    if (!segment.groupId) continue;
    const rawEnd = segment.end ?? segment.start;
    const displayedEnd = timeline?.segments.get(segment.id)?.end ?? rawEnd;
    if (!Number.isFinite(segment.start) || !Number.isFinite(rawEnd) || !Number.isFinite(displayedEnd)) continue;
    extents.set(segment.id, { start: segment.start, rawEnd, displayedEnd, appendPending: false });
  }
  return extents;
}

/** Row-owned clocks survive segment culling, fragment changes, and point-to-bar remounts. */
export function useSegmentGrowth(
  segments: readonly TRenderableSegment[],
  timeline: TrackingTimeline | null,
): ReadonlyMap<string, SegmentGrowth> {
  const [snapshot, setSnapshot] = useState(() => ({
    segments,
    timeline,
    extents: extentsFor(segments, timeline),
    growth: new Map<string, SegmentGrowth>(),
  }));
  if (snapshot.segments === segments && snapshot.timeline === timeline) return snapshot.growth;

  const extents = extentsFor(segments, timeline);
  const growth = new Map<string, SegmentGrowth>();
  const reset = (snapshot.timeline !== null && timeline === null) || snapshot.timeline?.revision !== timeline?.revision;
  const now = performance.now();
  if (!reset) {
    for (const [id, current] of extents) {
      const previous = snapshot.extents.get(id);
      if (!previous) {
        // Existing rows mount fully painted. A new duration cluster arriving
        // while this row is mounted may reveal from its first GT timestamp.
        if (current.rawEnd > current.start && current.displayedEnd > current.start) {
          growth.set(id, { from: current.start, to: current.displayedEnd, startedAt: now });
        }
        continue;
      }
      if (
        current.start !== previous.start ||
        current.rawEnd < previous.rawEnd ||
        current.displayedEnd < previous.displayedEnd
      )
        continue;

      const existing = snapshot.growth.get(id);
      const active = existing && now - existing.startedAt < SEGMENT_GROWTH_MS ? existing : undefined;
      current.appendPending = previous.appendPending || current.rawEnd > previous.rawEnd;
      if (current.displayedEnd > previous.displayedEnd) {
        if (current.appendPending) {
          const from = active ? segmentGrowthEnd(active, now) : previous.displayedEnd;
          if (from < current.displayedEnd) growth.set(id, { from, to: current.displayedEnd, startedAt: now });
          // Raw GT can be ahead of the 100 ms publication. Keep the remaining
          // append eligible until its timestamps reach the displayed envelope.
          current.appendPending = current.rawEnd > current.displayedEnd;
        }
        // A projection-only extent change is immediate, including any active
        // growth whose target no longer describes the displayed geometry.
      } else if (active) {
        growth.set(id, active);
      }
    }
  }
  setSnapshot({ segments, timeline, extents, growth });
  return growth;
}
